import React, { useState, useEffect, useRef } from 'react';
import { FaFilter, FaChevronDown, FaChevronUp, FaSearch } from 'react-icons/fa';
import { fetchAllMiGeLCodes } from '../services/apiService';
import { useLanguage } from '../contexts/LanguageContext';

// Custom Searchable Dropdown Component
const SearchableDropdown = ({ 
  options, 
  value, 
  onChange, 
  placeholder, 
  label,
  searchPlaceholder
}) => {
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const filteredOptions = options.filter(option => 
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedOption = options.find(option => option.value === value);

  return (
    <div className="relative" ref={dropdownRef}>
      <label className="block text-xs font-medium text-gray-700 mb-1">
        {label}
      </label>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
      >
        <span className={selectedOption ? 'text-gray-900' : 'text-gray-500'}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <FaChevronDown className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-hidden">
          {/* Search Input at the top of dropdown */}
          <div className="p-2 border-b border-gray-200">
            <div className="relative">
              <FaSearch className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder={searchPlaceholder || t('filters.migel.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-6 pr-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
            </div>
          </div>

          {/* Options List */}
          <div className="max-h-48 overflow-y-auto">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    onChange(option.value);
                    setIsOpen(false);
                    setSearchTerm('');
                  }}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 focus:bg-indigo-50 focus:outline-none ${
                    option.value === value ? 'bg-indigo-100 text-indigo-700' : 'text-gray-700'
                  }`}
                >
                  {option.label}
                </button>
              ))
            ) : (
              <div className="px-3 py-2 text-sm text-gray-500">
                {t('filters.migel.noOptionsFound')}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const MiGeLFilter = ({ onMiGeLCodeSelect, onClear }) => {
  const { t } = useLanguage();
  const [codes, setCodes] = useState([]);
  const [selectedCode, setSelectedCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const fetchCodes = async () => {
      try {
        setIsLoading(true);
        const response = await fetchAllMiGeLCodes();
        if (response.success) {
          setCodes(response.data?.categories || []);
        } else {
          setCodes([]);
        }
      } catch (error) {
        console.error('Error fetching MiGeL codes:', error);
        setCodes([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchCodes();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCodeChange = (code) => {
    setSelectedCode(code);
    if (code) {
      onMiGeLCodeSelect(code);
    } else {
      onClear();
    }
  };

  const handleClear = () => {
    setSelectedCode('');
    onClear();
  };

  // Prepare options for dropdown
  const codeOptions = codes.map(code => ({
    value: code.categoryCode,
    label: `${code.categoryCode} - ${code.categoryName} (${code.productCount} products)`
  }));

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="flex items-center gap-3">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="inline-flex items-center gap-2 bg-white border border-gray-300 rounded-lg px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
        >
          <FaFilter className="text-gray-400" />
          {t('filters.migel.title')}
          {isOpen ? <FaChevronUp className="text-gray-400" /> : <FaChevronDown className="text-gray-400" />}
        </button>
        {selectedCode && (
          <button
            onClick={handleClear}
            className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 transition-colors duration-200"
          >
            {t('filters.migel.clearFilter')}
          </button>
        )}
      </div>
      
      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-[400px] max-w-[90vw] bg-white border border-gray-200 rounded-lg shadow-lg z-[99999]" style={{ transform: 'translateZ(0)' }}>
          <div className="p-4">
            <h3 className="text-sm font-medium text-gray-900 mb-3">{t('filters.migel.modalTitle')}</h3>
            
            {isLoading ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                <span className="ml-2 text-sm text-gray-500">{t('filters.migel.loading')}</span>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Code Dropdown with built-in search */}
                <SearchableDropdown
                  options={codeOptions}
                  value={selectedCode}
                  onChange={handleCodeChange}
                  placeholder={t('filters.migel.codePlaceholder')}
                  label={t('filters.migel.codeLabel')}
                  searchPlaceholder={t('filters.migel.codeSearchPlaceholder')}
                />

                {/* Selected Filter Display */}
                {selectedCode && (
                  <div className="mt-3 p-2 bg-indigo-50 border border-indigo-200 rounded-md">
                    <p className="text-xs text-indigo-800">
                      <span className="font-medium">{t('filters.migel.selected')}</span>
                      <span className="ml-1">
                        {codes.find(cat => cat.categoryCode === selectedCode)?.categoryName}
                      </span>
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MiGeLFilter; 